import { ComponentProps, FC, useId } from 'react';
import styled, { css } from 'styled-components';
import { COLOURS, hasColor, shouldForwardProp } from '@belong/themes';
import { ColoursValue, IconSize, IIcon, IStyledIcon, SpotIconBackgroundColor } from '@belong/types';
import { ICONS } from '@belong/web-icons/dist/dynamic';

export const iconSizes: Record<IconSize, string> = {
  xxsmall: '1.6rem',
  xsmall: '2.0rem',
  small: '2.4rem',
  smallplus: '3.2rem',
  medium: '4.8rem',
  large: '7.2rem',
  xlarge: '10.0rem'
};

const spotIconColours: Record<SpotIconBackgroundColor, ColoursValue> = {
  BLACK: COLOURS.BELONG_BLUE_DARK,
  GREY_700: COLOURS.BELONG_BLUE_DARK,
  GREY_200: COLOURS.GREY_700
};

interface IStyledIconProps extends IStyledIcon {
  isSpotIcon: boolean;
}

export const StyledIcon = styled.span.withConfig({
  shouldForwardProp: shouldForwardProp<IStyledIconProps>(['size'])
})<IStyledIconProps>`
  --iconSize: ${({ size }) => iconSizes[size || 'small']};

  display: inline-block;
  flex-shrink: 0;
  width: var(--iconSize);
  height: var(--iconSize);

  ${hasColor};

  ${({ isSpotIcon, theme }) => {
    return (
      isSpotIcon &&
      css`
        color: ${spotIconColours[theme.icon.spotBackgroundColor]};
      `
    );
  }}

  svg {
    width: var(--iconSize);
    height: var(--iconSize);

    &:not([data-preserve-color='true']) path {
      fill: currentColor;
    }

    rect:first-of-type {
      ${({ isSpotIcon, theme }) =>
        isSpotIcon &&
        css`
          fill: ${COLOURS[theme.icon.spotBackgroundColor]};
        `}
    }
  }

  &.inline-icon {
    position: relative;
    top: 0.6rem;
  }
`;

export const Icon: FC<IIcon> = ({ name, ariaLabel, role, ...props }: IIcon) => {
  const SvgIcon = ICONS[name];

  const svgProps: ComponentProps<typeof SvgIcon> = {
    unique: useId()
  };

  // When using aria-label, the role should be set to img, but, if one is provided, use that instead
  if (ariaLabel) {
    svgProps['aria-label'] = ariaLabel;
    svgProps.role = role || 'img';
  } else if (role) {
    svgProps.role = role;
  }

  const isSpotIcon = name.endsWith('__Spot');

  return (
    <StyledIcon {...props} isSpotIcon={isSpotIcon}>
      <SvgIcon {...svgProps} />
    </StyledIcon>
  );
};

Icon.displayName = 'Icon';
